import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SensorsSensor, SensorsService } from 'src/app/api/generated';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import { DataChangedService } from 'src/app/data-changed.service';
import { SensorLocalizationConfig } from '../sensor-localization.types';
import * as uuid from 'uuid';

@Component({
    selector: 'app-sensor-localization-config',
    templateUrl: './sensor-localization-config.component.html',
    styleUrls: ['./sensor-localization-config.component.scss'],
    standalone: false
})
export class SensorLocalizationConfigComponent implements OnInit {
  public form: UntypedFormGroup;
  public editMode = false;
  public availableSensors: SensorsSensor[] = [];
  public submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SensorLocalizationConfig,
    private dialogRef: MatDialogRef<SensorLocalizationConfig>,
    private formBuilder: UntypedFormBuilder,
    private reportModuleService: ReportModulesService,
    private dataChangedService: DataChangedService,
    private sensorService: SensorsService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.editMode = this.data !== null;
    this.form = this.formBuilder.group({
      guid: [this.editMode ? this.data?.guid : uuid.v4()],
      type: ['sensor-localization', Validators.required],
      headline: [
        this.data?.headline ? this.data?.headline : '',
        Validators.required,
      ],
      datasources: this.formBuilder.array([]),
      allDatasourcesChecked: [false],
    });

    this.sensorService
      .sensorsProjectRead(this.reportModuleService.currentReport.project)
      .subscribe({
        next: (sensors) => {
          this.availableSensors = sensors.sort((a, b) =>
            a.display_name.localeCompare(b.display_name, undefined, {
              numeric: true,
            })
          );

          this.availableSensors.forEach((sensor) => {
            (this.form.get('datasources') as FormArray).push(
              new FormGroup({
                id: new UntypedFormControl(sensor.id, Validators.required),
                name: new UntypedFormControl(sensor.display_name),
                checked: new UntypedFormControl(this.isSensorChecked(sensor)),
              })
            );
          });

          // Sync master checkbox
          this.datasourcesFromFormGroup().controls.forEach((control) => {
            control.valueChanges.subscribe(() => {
              const allChecked = this.datasourcesFromFormGroup().controls.every(
                (candidate) => candidate.get('checked').value
              );
              if (this.form.get('allDatasourcesChecked').value !== allChecked) {
                this.form
                  .get('allDatasourcesChecked')
                  .setValue(allChecked, { emitEvent: false });
              }
            });
          });
        },
        error: () => {
          this.snackBar.open('Fehler beim Laden der Sensoren', 'OK', {
            duration: 3000,
          });
        },
      });
  }

  private isSensorChecked(sensor: SensorsSensor): boolean {
    if (!this.editMode) {return false;}
    const entry = this.data.datasources?.find(
      (ds) => ds.id === sensor.id || ds.id === sensor.influx_name
    );
    return entry ? entry.checked : false;
  }

  get datasourcesFromFormGroup(): () => UntypedFormArray {
    return () => this.form.get('datasources') as UntypedFormArray;
  }

  public onAllDatasourcesCheckedChange(event: any): void {
    const isChecked = !!event?.checked;
    this.datasourcesFromFormGroup().controls.forEach((ctrl) => {
      ctrl.get('checked').setValue(isChecked);
    });
  }

  public updateOrCreateModule() {
    if (!this.form.valid) {
      return;
    }

    this.submitting = true;
    const payload: SensorLocalizationConfig = {
      guid: this.form.value.guid,
      type: this.form.value.type,
      headline: this.form.value.headline,
      datasources: this.form.value.datasources,
      user_settings: {},
    };

    this.reportModuleService
      .addOrEditModuleToReport(<any>payload, this.editMode)
      .subscribe(
        (success) => {
          this.submitting = false;
          this.dialogRef.close();
          this.dataChangedService.configUpdated(
            this.reportModuleService.currentReport.id,
            this.form.value.guid
          );
        },
        (error) => {
          this.submitting = false;
          this.snackBar.open('Fehler beim Speichern der Konfiguration', 'OK', {
            duration: 3000,
          });
        }
      );
  }
}
